import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import _ from "lodash";

import {
    onSetOffline,
    onSetSelectedConsumptionDetails,
    onDtStartChange,
    onDtEndChange,
    onClearConsumptionDetails,
    getActivityAndConsumptionDetailsByOperationIdRedux,
    getActivityAndConsumptionDetailsByLineIdRedux,
    closeConsumptionGetPalletDetails,
    deleteConsumptionGetPalletDetails
} from "../../redux/slices/consumptionDetails.feature";

import { getConsumptionDetailsColumns } from "./ConsumptionDetailsColumns";
import { DtGrid2 } from "../common/DtGrid2";
import AplSettingsSelector from "../../redux/helpers/AplSettingsSelector";
import ConsumptionDetailsBottomButttons from "./ConsumptionDetailsBottomButttons";
import { bootboxConfirm } from "../common/bootboxWrapper";
import ConsumptionDetailsRightClick from "./ConsumptionDetailRightClick";

import { IRootState } from "../../types/redux/IRootStore";
import { ConsumptionDetail } from "../../types/consumptionDetail";
import { Guid } from "guid-typescript";
import { Activity } from "../../types/activity";
import { useAppDispatch } from "../../redux/hooks";
import { DtGridPropsOptions2 } from "../../types/dtGridPropsOptions2";
import { useTranslation } from "react-i18next";
import { getExceptionText, translateColumns, translateString } from "../../translations/gridTranslationHelper";
import { useNonInitialEffect } from "../../hooks/useNonInitialEffect";
import { getTextColor } from "../../aplUtils/themeHelper";
import { AplParameter } from "../../types/aplParameter";
import { parseBool } from "../../aplUtils/extensions";
import { showModalWithConsumptionState, showModalWithPalletsByGuid } from "../../redux/slices/modalPallets2.feature";
import { ModalType } from "../common/Modals/types/modalType";
import { UserPermission } from "../../types/userPermission";
import { clearWorkorderManagement } from "../../redux/slices/workorderItems.feature";
import DateSelector3 from "../common/DateSelector3";
import ConsumptionDetailsDefectsTable from "./ConsumptionDetailsDefectsTable";

const ConsumptionDetails = () => {
    const aplSettings = AplSettingsSelector();
    const { t } = useTranslation();
    let params = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [barcode, setBarcode] = useState("");
    const consumptionDetailsRedux = useSelector((state: IRootState) => state.consumptionDetails);
    const authRedux = useSelector((state: IRootState) => state.auth);
    const [page, setPage] = useState<number>(1);

    const dtGridOptns: DtGridPropsOptions2 = {
        data: consumptionDetailsRedux.consumptionDetails,
        columnData: translateColumns(getConsumptionDetailsColumns(aplSettings.siteId, aplSettings.culture), aplSettings.culture),
        defaultSort: {
            field: ["dtoDateIssued"],
            direction: ["desc"]
        },
        selectType: "single",
        colouring: false,
        headerFilters: false,
        setSelectedItems: (x: ConsumptionDetail[]) => {
            dispatch(onSetSelectedConsumptionDetails(x));
        },
        selectedItems: consumptionDetailsRedux.selectedConsumptionDetails,
        tableName: "tblConsumptionDetails",
        tableClass: "table-consumption-details",
        paging: true,
        showSpinner: !consumptionDetailsRedux.consumptionDetailsFirstLoaded,
        pageLength: aplSettings.siteId === 7 ? 3 : 12,
        rowId: "consumptionId",
        loading: consumptionDetailsRedux.consumptionDetailsLoading,
        filters: {},
        setFilters: _.noop,
        page: page,
        setPage: setPage
    };

    useNonInitialEffect(() => {
        console.log(`${new Date().toISOString()} useNonInitialEffect on consumptionDetailsRedux.offline`);
        dispatch(onDtStartChange(moment().subtract(1, "hours")));
        dispatch(onDtEndChange(moment()));
    }, [consumptionDetailsRedux.offline]);

    const rightClickOptions = {
        return:
            parseBool(aplSettings.siteParams.find(x => x.aplParameterType === AplParameter.AllowPartialConsumption)?.value ?? "false") &&
            consumptionDetailsRedux.selectedConsumptionDetails.length === 1 &&
            consumptionDetailsRedux.selectedConsumptionDetails[0].dtoDateConsumed == null,
        consume:
            consumptionDetailsRedux.selectedConsumptionDetails.length === 1 &&
            consumptionDetailsRedux.selectedConsumptionDetails[0].dtoDateConsumed == null,
        delete:
            consumptionDetailsRedux.selectedConsumptionDetails.length === 1 &&
            consumptionDetailsRedux.selectedConsumptionDetails[0].sentToSap === 0,
        block:
            consumptionDetailsRedux.selectedConsumptionDetails.length === 1 &&
            consumptionDetailsRedux.selectedConsumptionDetails[0].dtoDateConsumed == null,
        returnFunc: () => {
            dispatch(
                showModalWithPalletsByGuid({
                    pallets: consumptionDetailsRedux.selectedConsumptionDetails.map(x => Guid.parse(x.palletId)),
                    modalType: ModalType.ConsumptionReturn,
                    callerModalType: ModalType.None
                }) as any
            );
        },
        consumeFunc: () => {
            if (consumptionDetailsRedux.selectedConsumptionDetails.length === 1) {
                dispatch(
                    closeConsumptionGetPalletDetails({
                        consumptionId: consumptionDetailsRedux.selectedConsumptionDetails[0].consumptionId,
                        operationId: consumptionDetailsRedux.currentActivity[0].operationId
                    }) as any
                );
            }
        },
        deleteFunc: () => {
            if (consumptionDetailsRedux.selectedConsumptionDetails.length === 1) {
                bootboxConfirm(translateString("deleteConsumption", aplSettings.culture), null, (response: boolean) => {
                    if (response) {
                        dispatch(
                            deleteConsumptionGetPalletDetails({
                                consumptionId: consumptionDetailsRedux.selectedConsumptionDetails[0].consumptionId,
                                operationId: consumptionDetailsRedux.currentActivity[0].operationId
                            }) as any
                        );
                    }
                });
            }
        },
        blockFunc: () => {
            if (consumptionDetailsRedux.selectedConsumptionDetails.length === 1) {
                dispatch(
                    showModalWithPalletsByGuid({
                        pallets: consumptionDetailsRedux.selectedConsumptionDetails.map(x => Guid.parse(x.palletId)),
                        modalType: ModalType.Defects,
                        callerModalType: ModalType.None
                    }) as any
                );
            }
        }
    };

    const handleScanFirst = async (barcode: string) => {
        if (barcode.trim().length > 5)
            dispatch(
                showModalWithConsumptionState({
                    operationId: consumptionDetailsRedux.currentActivity[0].operationId,
                    barcode: barcode
                }) as any
            );
        setBarcode("");
    };

    const displayedActivity = consumptionDetailsRedux.currentActivity[0] as Activity;

    const handleChangeOfflineMode = React.useCallback(
        (isOffline: boolean, redirect: boolean = true) => {
            // console.log(`${new Date().toISOString()} handleChangeOfflineMode`, isOffline, redirect);
            dispatch(onSetOffline(isOffline));
            if (isOffline && displayedActivity != null) {
                if (redirect) navigate(`/consumption/${displayedActivity.lineId}/${displayedActivity.operationId}`);
            }
            if (!isOffline && displayedActivity != null) {
                if (redirect) navigate(`/consumption/${displayedActivity.lineId}`);
            }
        },
        [dispatch, displayedActivity, navigate]
    );

    useEffect(() => {
        console.log(`${new Date().toISOString()} useEffect on params`, params);
        // debugger;
        if (params["operationId"] != null) {
            dispatch(getActivityAndConsumptionDetailsByOperationIdRedux(params.operationId) as any);
            handleChangeOfflineMode(true, false);
        } else {
            dispatch(getActivityAndConsumptionDetailsByLineIdRedux(Number(params.lineId)) as any);
            handleChangeOfflineMode(false, false);
        }

        dispatch(onDtStartChange(moment().subtract(1, "hours")));
        dispatch(onDtEndChange(moment()));

        return () => {
            dispatch(onClearConsumptionDetails());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consumptionDetailsRedux.offline]);

    return (
        <React.Fragment>
            <div
                id="div-error"
                className={`alert alert-danger ${consumptionDetailsRedux.consumptionDetailsError ? "" : "hide"}`}
                role="alert"
            >
                {getExceptionText(consumptionDetailsRedux.consumptionDetailsError, aplSettings.culture)}
            </div>

            <div className="container mw-100">
                <div className="row">
                    <div className="col">
                        <h2
                            className="rounded-lg pl-2"
                            style={{
                                backgroundColor: aplSettings.theme.consumptionColour ? aplSettings.theme.consumptionColour : "inherit",
                                color: aplSettings.theme.consumptionColour ? getTextColor(aplSettings.theme.consumptionColour) : "inherit"
                            }}
                        >
                            {t("main.rxg_Consumption")}
                        </h2>

                        <div className="row">
                            <div className="col-6">
                                <div className="row">
                                    <div className="col-3">
                                        <span>{t("main.workCenter")}</span>
                                    </div>
                                    <div className="col-9">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="txtWorkCenter"
                                            aria-describedby="WorkCenter"
                                            readOnly={true}
                                            value={
                                                !(
                                                    consumptionDetailsRedux.currentActivityError ||
                                                    consumptionDetailsRedux.currentActivityLoading
                                                )
                                                    ? `${consumptionDetailsRedux.currentActivity[0]?.lineSapCode} / ${consumptionDetailsRedux.currentActivity[0]?.linePlantCode}`
                                                    : "..."
                                            }
                                            onChange={_.noop}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-3">
                                        <span>{t("main.workOrder")}</span>
                                    </div>
                                    <div className="col-9">
                                        <div className="input-group mb-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="txtWorkOrder"
                                                readOnly={true}
                                                value={
                                                    !(
                                                        consumptionDetailsRedux.currentActivityError ||
                                                        consumptionDetailsRedux.currentActivityLoading
                                                    )
                                                        ? consumptionDetailsRedux.currentActivity[0]?.workOrder ?? ""
                                                        : "..."
                                                }
                                                onChange={_.noop}
                                            />
                                            {consumptionDetailsRedux.offline && (
                                                <div className="input-group-append">
                                                    <button
                                                        id="btn-save-number-of-copies"
                                                        className="btn btn-sm btn-primary"
                                                        type="button"
                                                        onClick={() => {
                                                            dispatch(clearWorkorderManagement());
                                                            navigate("/workordermanagement");
                                                        }}
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="16"
                                                            height="16"
                                                            fill="currentColor"
                                                            className="bi bi-search"
                                                            viewBox="0 0 16 16"
                                                        >
                                                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-3">
                                        <span>{t("main.material")}</span>
                                    </div>
                                    <div className="col-9">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="txtMaterial"
                                            readOnly={true}
                                            value={
                                                !(
                                                    consumptionDetailsRedux.currentActivityError ||
                                                    consumptionDetailsRedux.currentActivityLoading
                                                )
                                                    ? `${consumptionDetailsRedux.currentActivity[0]?.materialSapCode}: ${consumptionDetailsRedux.currentActivity[0]?.description1}`
                                                    : "..."
                                            }
                                        />
                                    </div>
                                </div>
                                {aplSettings.siteId > 0 && aplSettings.siteId <= 4 ? (
                                    <div className="row" id="speakingPartNo">
                                        <div className="col-3">
                                            <span>{t("main.material12")}</span>
                                        </div>
                                        <div className="col-9">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="txtMaterial"
                                                readOnly={true}
                                                value={
                                                    !(
                                                        consumptionDetailsRedux.currentActivityError ||
                                                        consumptionDetailsRedux.currentActivityLoading
                                                    )
                                                        ? `${consumptionDetailsRedux.currentActivity[0]?.speakingPartNo ?? ""}`
                                                        : "..."
                                                }
                                            />
                                        </div>
                                    </div>
                                ) : null}

                                {aplSettings.isUsSite ? (
                                    <div className="row">
                                        <div className="col-3">
                                            <span>{t("main.pallets")}</span>
                                        </div>
                                        <div className="col-9">
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="txt-issued-consumed"
                                                readOnly={true}
                                                value={
                                                    !(
                                                        consumptionDetailsRedux.currentActivityError ||
                                                        consumptionDetailsRedux.currentActivityLoading
                                                    )
                                                        ? `${consumptionDetailsRedux.currentActivity[0]?.consumedPallets} ${translateString(
                                                              "consumed",
                                                              aplSettings.culture
                                                          )} / ${
                                                              consumptionDetailsRedux.currentActivity[0]?.issuedPallets
                                                          } ${translateString("issued", aplSettings.culture)}`
                                                        : "..."
                                                }
                                            />
                                        </div>
                                    </div>
                                ) : null}

                                <div className="row" id="offlineConsSwitch">
                                    <div className="col">
                                        <div className="row">
                                            <div className="col">&nbsp;</div>
                                        </div>

                                        <div className="row">
                                            <div className="col">
                                                <div className="custom-control custom-switch bs-switch">
                                                    <input
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        id="swOffline"
                                                        checked={consumptionDetailsRedux.offline}
                                                        disabled={
                                                            !aplSettings.hasPermByLineId(
                                                                UserPermission.OfflineLabelling,
                                                                Number(params.lineId)
                                                            )
                                                        }
                                                        onChange={e => {
                                                            handleChangeOfflineMode(e.target.checked, true);
                                                        }}
                                                    />
                                                    <label className="custom-control-label" htmlFor="swOffline">
                                                        {t("main.offlineCons")}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={consumptionDetailsRedux.offline ? "" : "d-none"} id="div-offline">
                                    <br />

                                    <div className="row" id="offlineStartDate">
                                        <div className="col-3">
                                            <span>{t("main.startDate")}</span>
                                        </div>
                                        <div className="col-6">
                                            <DateSelector3
                                                onSetSelectedDate={e => {
                                                    dispatch(onDtStartChange(e));
                                                }}
                                                selectedDate={moment(consumptionDetailsRedux.dateStartSelectorDate).toDate()}
                                                culture={aplSettings.culture}
                                            />
                                        </div>
                                    </div>

                                    <div className="row" id="offlineEndDate">
                                        <div className="col-3">
                                            <span>{t("main.endDate")}</span>
                                        </div>
                                        <div className="col-6">
                                            <DateSelector3
                                                onSetSelectedDate={e => {
                                                    dispatch(onDtEndChange(e));
                                                }}
                                                selectedDate={moment(consumptionDetailsRedux.dateEndSelectorDate).toDate()}
                                                culture={aplSettings.culture}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-6">
                                {/* <div className="row" ref={tableDivRef}>
                                    <table
                                        id="consDefectTable"
                                        style={{
                                            minWidth: (tableDivRef.current?.clientWidth ?? 0) - 16 ?? 0,
                                            position: "absolute",
                                            maxWidth: (tableDivRef.current?.clientWidth ?? 100) - 16
                                        }}
                                    >
                                        <tr>
                                            <th>{translateString("rxg_defectsTable_Defect", aplSettings.culture)}</th>
                                            <th>{translateString("mouldNo", aplSettings.culture)}</th>
                                            <th>{translateString("comment", aplSettings.culture)}</th>
                                            <th>{translateString("initials", aplSettings.culture)}</th>
                                        </tr>

                                        {consumptionDetailsRedux.selectedConsumptionDetails[0]?.defects?.map(d => (
                                            <tr>
                                                <td>{renderSingleDefectName(d.defectId, authRedux.defectTypes)}</td>
                                                <td>{d.mouldNo}</td>
                                                <td>{d.comment}</td>
                                                <td>{d.initials}</td>
                                            </tr>
                                        ))}
                                    </table>
                                </div> */}
                                <ConsumptionDetailsDefectsTable
                                    selectedConsumptionDetails={consumptionDetailsRedux.selectedConsumptionDetails}
                                    culture={aplSettings.culture}
                                    defectTypes={authRedux.defectTypes}
                                />
                            </div>
                        </div>

                        <div className="row">&nbsp;</div>

                        <DtGrid2 options={dtGridOptns}>
                            <ConsumptionDetailsBottomButttons
                                handleClick={handleScanFirst}
                                barcode={barcode}
                                setBarcode={setBarcode}
                                {...rightClickOptions}
                            />
                            <ConsumptionDetailsRightClick options={rightClickOptions} />
                        </DtGrid2>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ConsumptionDetails;
